/* eslint-disable react/no-this-in-sfc */
import React, { useState } from 'react';
import Highcharts, { TooltipFormatterContextObject } from 'highcharts';
import moment from 'moment';
import { AreaSplineChart } from '../../../CommonComponents/OPL/charts/AreaSplineChart/AreaSplineChart';
import { theme } from '../../../theme/theme';
import { formatCurrency, formatPercentage } from '../../../CommonUtils/formatNumber';
import { useAmountInvestedChart } from '../../hooks/useAmountInvestedChart';
import { ErrorState } from '../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { AreaSplineChartLoading } from '../../../CommonComponents/OPL/charts/AreaSplineChart/AreaSplineChart.loading';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { EmptyState } from '../../../CommonComponents/OPL/AsyncStates/EmptyState/EmptyState';
import { PeriodSegmentedControl } from '../PeriodSegmentedControl/PeriodSegmentedControl';
import { LegendIndicator } from './ValueVsInvestmentChart.styles';
import { useIsEmbeddedMobile } from '../../../_hooks/useIsEmbeddedMobile';
import { RefreshButton } from '../RefreshButton/RefreshButton';

interface ValueVsInvestmentChartProps {
  title: string;
  portfolioReturn: number | null;
  updatedAt: string;
}

export const ValueVsInvestmentChart = ({
  title,
  portfolioReturn,
  updatedAt,
}: ValueVsInvestmentChartProps) => {
  const isEmbeddedMobile = useIsEmbeddedMobile();

  const { isFetching, isError, refetch, data } = useAmountInvestedChart();

  const [selectedPoints, setSelectedPoints] = useState<TooltipFormatterContextObject[]>([]);

  if (isFetching) {
    return <AreaSplineChartLoading containerProps={{ style: { height: 'unset' } }} />;
  }

  if (isError) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
        <ErrorState onAction={refetch} />
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  if (data.length === 0) {
    return (
      <Box height="100%" display="flex" alignItems="center" justifyContent="center">
        <EmptyState />
      </Box>
    );
  }

  const customSettings: Highcharts.Options = {
    series: [
      {
        color: theme.colors.primary['100'],
        fillColor: theme.colors.primary['100'],
        lineWidth: 0,
        marker: {
          symbol: 'circle',
        },
        type: 'areaspline',
      },
      {
        color: theme.colors.primary['600'],
        fillColor: 'transparent',
        lineWidth: 2,
        marker: {
          symbol: 'circle',
        },
        type: 'areaspline',
      },
    ],

    chart: {
      height: isEmbeddedMobile ? '45%' : undefined,
      spacing: [10, 10, 15, 10],
      marginRight: 0,
    },

    tooltip: {
      style: {
        opacity: isEmbeddedMobile ? 0 : 1,
      },

      shared: true,

      formatter() {
        if (isEmbeddedMobile) {
          setSelectedPoints(this.points ?? []);

          return null;
        }

        let tooltip = `
        <span style="font-size: 10px">
          ${moment.utc(Number((this as any).x)).format('ll')}
        </span><table>`;

        if ((this as any).points) {
          (this as any).points.forEach(({ y, color, series: { name } }) => {
            tooltip += `
              <tr>
                <td>
                  <span style="color:red">\u25CF</span> ${name}:
                </td> 
                <td>
                  <span style="font-weight: 600">
                    ${formatCurrency(Number(y))}
                  </span>
                </td>
              </tr>
            `;
          });
        }

        return `${tooltip}</table>`;
      },
    },

    legend: { enabled: !isEmbeddedMobile },

    xAxis: {
      labels: {
        align: 'right',
        overflow: 'justify',
        padding: 12,
        formatter() {
          const date = new Date(this.value);
          return `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${date.getUTCFullYear()}`;
        },
      },
      minPadding: 0.01, // ignored when using min
      maxPadding: 0.01, // ignored when using max
      min: data[0].data[0][0],
      max: data[0].data[data[0].data.length - 1][0],
      tickPixelInterval: 50,
    },

    yAxis: {
      gridLineWidth: isEmbeddedMobile ? 0 : 1,
      showFirstLabel: !isEmbeddedMobile,
      labels: {
        align: 'right',
        x: -8,
        formatter() {
          return formatCurrency(Number(this.value), {
            average: true,
            mantissa: 2,
            optionalMantissa: isEmbeddedMobile,
            trimMantissa: isEmbeddedMobile,
          }).toUpperCase();
        },
      },
    },

    plotOptions: {
      series: {
        events: {
          mouseOut() {
            this.chart.tooltip.hide();

            setSelectedPoints([]);
          },
        },
      },
    },
  };

  const [portfolioValue, netAmountInvested] = data;
  const [selectedPortfolioValue, selectedNetAmountInvested] = selectedPoints;

  return (
    <>
      {isEmbeddedMobile && (
        <Box p="16" pt="0">
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Text isOverflowWrap variant="smallCaps" mr="8">
              {title}
            </Text>

            <RefreshButton />
          </Box>

          <Text
            variant="tabular"
            fontSize="h2"
            fontWeight={500}
            color={selectedPoints.length > 0 ? 'primary.500' : 'neutral.700'}
            mt={-6}
          >
            {formatCurrency(
              selectedPortfolioValue?.y ?? portfolioValue.data[portfolioValue.data.length - 1][1],
            )}
          </Text>

          <Box display="flex">
            {portfolioReturn !== null && selectedPoints.length === 0 && (
              <Text
                fontSize="small"
                color={portfolioReturn >= 0 ? 'success.500' : 'error.500'}
                mr="8"
                fontWeight={600}
              >
                {formatPercentage(portfolioReturn / 100, { forceSign: true, decimalPlaces: 2 })}
              </Text>
            )}

            <Text
              fontSize="small"
              color={selectedPoints.length > 0 ? 'primary.500' : 'neutral.500'}
            >
              {selectedPoints.length > 0
                ? moment(
                    selectedPortfolioValue?.x ??
                      portfolioValue.data[portfolioValue.data.length - 1][0],
                  ).format('MM/DD/YYYY')
                : updatedAt}
            </Text>
          </Box>

          <Box display="flex" alignItems="flex-end" mt="8">
            <LegendIndicator />

            <Box>
              <Text
                fontWeight={600}
                color={selectedPoints.length > 0 ? 'primary.500' : 'neutral.700'}
              >
                {formatCurrency(
                  selectedNetAmountInvested?.y ??
                    netAmountInvested.data[netAmountInvested.data.length - 1][1],
                )}
              </Text>
              <Text fontSize="small" fontWeight={500}>
                {netAmountInvested.name}
              </Text>
            </Box>
          </Box>
        </Box>
      )}

      <Box px={isEmbeddedMobile ? 16 : 0} py="0">
        <AreaSplineChart
          series={data}
          customSettings={customSettings}
          xAxisType="datetime"
          yAxisType="currency"
          containerProps={{ style: { height: 'unset' } }}
        />
      </Box>

      <Box px={isEmbeddedMobile ? 16 : 0}>
        <PeriodSegmentedControl />
      </Box>
    </>
  );
};
